import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Clock, Truck, MessageSquare, Printer, Sparkles, Store } from 'lucide-react';
import PrinterSettings from "./PrinterSettings";
import SettingsWhatsAppTab from '@/components/settings/SettingsWhatsAppTab';
import RestaurantLogoUpload from '@/components/settings/RestaurantLogoUpload';
import RestaurantDeliveryOrigin, { validOrigin } from '@/components/settings/RestaurantDeliveryOrigin';
import { restaurantCurrencyLabel } from '../lib/restaurantCurrency';

const API = API_BASE_URL;
const DEFAULT_DELIVERY_SETTINGS = {
  enabled: false, min_order: 10, delivery_fee: 5, free_delivery_threshold: 50,
  delivery_radius_km: 5, estimated_time_minutes: 45,
  distance_tiers: [],
};
const plainObject = value => value !== null && typeof value === 'object' &&
  (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
const DELIVERY_READ_FIELDS = new Set([
  'pricing_mode', 'distance_tiers', 'delivery_location', 'enabled', 'min_order',
  'delivery_fee', 'free_delivery_threshold', 'delivery_radius_km', 'estimated_time_minutes', 'radius_km',
]);
const TIER_FIELDS = new Set(['up_to_km', 'fee']);
function normalizeDeliverySettings(settings) {
  const invalidSettings = settings != null && !plainObject(settings);
  const entries = Object.entries(invalidSettings ? {} : settings || {});
  // Validate stored semantics before defaults or structural repair hide missing fields.
  const stored = Object.fromEntries(entries);
  const radiusConflict = 'radius_km' in stored && 'delivery_radius_km' in stored && stored.radius_km !== stored.delivery_radius_km;
  if (!('delivery_radius_km' in stored) && 'radius_km' in stored) stored.delivery_radius_km = stored.radius_km;
  const semanticError = storedDeliveryPricingError(stored);
  let unsupportedFields = entries.some(([field]) => !DELIVERY_READ_FIELDS.has(field));
  const normalized = Object.fromEntries(entries.filter(([field]) => DELIVERY_READ_FIELDS.has(field)));
  const invalidPricingMode = Object.prototype.hasOwnProperty.call(normalized, 'pricing_mode') &&
    !['fixed', 'distance_tiers'].includes(normalized.pricing_mode);
  const invalidEnabled = Object.prototype.hasOwnProperty.call(normalized, 'enabled') &&
    normalized.enabled !== null && typeof normalized.enabled !== 'boolean';
  if (invalidPricingMode) delete normalized.pricing_mode;
  if (invalidEnabled) delete normalized.enabled;
  const invalidTiers = Object.prototype.hasOwnProperty.call(normalized, 'distance_tiers') &&
    (!Array.isArray(normalized.distance_tiers) || !normalized.distance_tiers.every(plainObject));
  if (invalidTiers) normalized.distance_tiers = [];
  else if (Array.isArray(normalized.distance_tiers)) {
    normalized.distance_tiers = normalized.distance_tiers.map(tier => {
      const tierEntries = Object.entries(tier);
      if (tierEntries.some(([field]) => !TIER_FIELDS.has(field))) unsupportedFields = true;
      return Object.fromEntries(tierEntries.filter(([field]) => TIER_FIELDS.has(field)));
    });
  }
  if (!Object.prototype.hasOwnProperty.call(normalized, 'delivery_radius_km') &&
      Object.prototype.hasOwnProperty.call(normalized, 'radius_km')) {
    normalized.delivery_radius_km = normalized.radius_km;
  }
  delete normalized.radius_km;
  return {
    settings: { ...DEFAULT_DELIVERY_SETTINGS, ...normalized },
    invalidPricing: invalidSettings || invalidTiers || unsupportedFields || invalidPricingMode || invalidEnabled || radiusConflict || Boolean(semanticError),
  };
}

function storedDeliveryPricingError(settings) {
  const number = value => typeof value === 'number' && Number.isFinite(value);
  if (Object.prototype.hasOwnProperty.call(settings, 'pricing_mode') && !['fixed', 'distance_tiers'].includes(settings.pricing_mode)) return 'Choose a valid delivery pricing method.';
  if (settings.enabled != null && typeof settings.enabled !== 'boolean') return 'Delivery enabled must be on, off, or unset.';
  for (const field of ['min_order', 'free_delivery_threshold']) {
    if (settings[field] != null && (!number(settings[field]) || settings[field] < 0)) return 'Enter valid nonnegative delivery amounts.';
  }
  if ((settings.delivery_radius_km != null || settings.pricing_mode === 'distance_tiers') && (!number(settings.delivery_radius_km) || settings.delivery_radius_km <= 0)) return 'Enter a positive delivery radius.';
  if (settings.estimated_time_minutes != null && (!Number.isInteger(settings.estimated_time_minutes) || settings.estimated_time_minutes <= 0)) return 'Enter a positive whole number of minutes.';
  if (settings.delivery_fee != null && (!number(settings.delivery_fee) || settings.delivery_fee < 0)) return 'Enter a valid nonnegative delivery fee.';
  if (settings.pricing_mode !== 'distance_tiers' && (settings.pricing_mode === 'fixed' || settings.enabled === true) && settings.delivery_fee == null) return 'Enter a valid nonnegative delivery fee.';
  const tiers = Object.prototype.hasOwnProperty.call(settings, 'distance_tiers') ? settings.distance_tiers : [];
  if (!Array.isArray(tiers) || tiers.length > 10) return 'Use at most 10 distance tiers.';
  if (!tiers.every(plainObject)) return 'Replace the invalid distance pricing tiers.';
  if (tiers.some(tier => !number(tier.up_to_km) || tier.up_to_km <= 0 || !number(tier.fee) || tier.fee < 0)) return 'Each tier needs a positive distance and a nonnegative fee.';
  if (tiers.some((tier, index) => index > 0 && tier.up_to_km <= tiers[index - 1].up_to_km)) return 'Tier distances must be strictly increasing, without duplicates.';
  if (settings.pricing_mode === 'distance_tiers') {
    if (!tiers.length) return 'Add at least one distance tier.';
    if (tiers[tiers.length - 1].up_to_km < settings.delivery_radius_km) return 'The final tier must cover the delivery radius.';
  }
  return null;
}

function deliveryPricingError(settings) {
  const pricingError = storedDeliveryPricingError(settings);
  if (pricingError) return pricingError;
  if (settings.pricing_mode === 'distance_tiers' && !validOrigin(settings.delivery_location)) {
    return 'Configure a verified restaurant origin before saving distance pricing.';
  }
  return null;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DEFAULT_PRINTER_ROLES = {
  kitchen: {
    enabled: true,
    printer_name: '',
    printer_type: 'thermal',
    printer_connection: 'usb',
    paper_size: '80',
    ip_address: '',
    usb_device: '',
    bluetooth_device: ''
  },
  cashier: {
    enabled: true,
    printer_name: '',
    printer_type: 'thermal',
    printer_connection: 'usb',
    paper_size: '80',
    ip_address: '',
    usb_device: '',
    bluetooth_device: ''
  },
  bar: {
    enabled: false,
    printer_name: '',
    printer_type: 'thermal',
    printer_connection: 'usb',
    paper_size: '80',
    ip_address: '',
    usb_device: '',
    bluetooth_device: ''
  },
  dessert: {
    enabled: false,
    printer_name: '',
    printer_type: 'thermal',
    printer_connection: 'usb',
    paper_size: '80',
    ip_address: '',
    usb_device: '',
    bluetooth_device: ''
  }
};

function createRestaurantInfo() {
  return {
  name: '',

  phone: '',
  address: '',

  currency: 'USD',

  whatsapp_number: '',
  kitchen_whatsapp_number: '',

  tax_enabled: false,
tax_rate: 0,

printer_enabled: false,
printer_type: "thermal",
printer_connection: "usb",
paper_size: "80",
printer_name: "",
auto_print: true,
copies: 1,
printer_roles: Object.fromEntries(Object.entries(DEFAULT_PRINTER_ROLES).map(([role, settings]) => [role, { ...settings }])),
printer_auto_print: {
  manual_orders: false,
  whatsapp_orders: false,
  online_orders: false,
},
printer_copies_by_role: {
  kitchen: 1,
  cashier: 1,
},
printer_diagnostics: {
  configuration_status: 'Not Configured',
  agent_status: 'Never Connected',
  hardware_status: 'Unknown',
  automatic_delivery_status: 'Inactive - Print Agent required',
  agent_last_seen_at: null,
},
};
}

function createBusinessHours() {
  return DAYS.reduce((acc, day) => ({ ...acc, [day]: { open: '09:00', close: '22:00', closed: false } }), {});
}

const SERVICE_ORDERS = ['dine_in', 'pickup', 'delivery'];
function serviceErrorFor(s) {
  if (s == null) return '';
  if (!plainObject(s) || Object.keys(s).some(k => !['enabled', 'type', 'value', 'applies_to'].includes(k))) return 'Malformed service charge settings.';
  if (s.enabled != null && typeof s.enabled !== 'boolean') return 'Invalid enabled setting.';
  if (s.type != null && !['percentage', 'fixed'].includes(s.type)) return 'Choose Percentage or Fixed.';
  if (s.value != null && (typeof s.value !== 'number' || !Number.isFinite(s.value) || s.value < 0)) return 'Enter a valid nonnegative value.';
  if (s.type === 'percentage' && s.value > 100) return 'Percentage cannot exceed 100.';
  if (s.applies_to != null && (!Array.isArray(s.applies_to) || s.applies_to.some(t => !SERVICE_ORDERS.includes(t)))) return 'Invalid order types.';
  if (s.enabled === true && (!s.type || s.value == null || !s.applies_to?.length)) return 'Choose a type, value and at least one order type.';
  return '';
}

const Settings = () => {
  const { t } = useTranslation();
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const restaurantScope = useRef(null);
  const [detailsStatus, setDetailsStatus] = useState('loading');
  const [loadedRestaurantId, setLoadedRestaurantId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [originUncertain, setOriginUncertain] = useState(false);
  const [originInvalid, setOriginInvalid] = useState(false);
  const [originCooldownUntil, setOriginCooldownUntil] = useState(0);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const canEditDelivery = ['super_admin', 'company_admin', 'restaurant_manager'].includes(user?.role);

  const [restaurantInfo, setRestaurantInfo] = useState(createRestaurantInfo);
  const [businessHours, setBusinessHours] = useState(createBusinessHours);

  const [deliverySettings, setDeliverySettings] = useState(DEFAULT_DELIVERY_SETTINGS);
  const [deliveryCurrency, setDeliveryCurrency] = useState('');
  const [serviceCharge, setServiceCharge] = useState(null);
  const [serviceError, setServiceError] = useState('');
  const [serviceMalformed, setServiceMalformed] = useState(false);
  const [serviceSaving, setServiceSaving] = useState(false);
  const editService = patch => { setServiceCharge(previous => ({ ...previous, ...patch })); setServiceError(''); };
  const [pricingError, setPricingError] = useState('');
  const [storedPricingInvalid, setStoredPricingInvalid] = useState(false);
  const [pricingRepairPending, setPricingRepairPending] = useState(false);
  const updatePricing = (next, replacesStructure = false) => {
    setDeliverySettings(next);
    const current = restaurantScope.current;
    if (replacesStructure && current) { current.pricingRepairRequested = true; current.pricingEdited = true; }
    if (current?.invalidPricing && current.pricingRepairRequested && !deliveryPricingError(next)) {
      current.invalidPricing = false;
      current.pricingRepairPending = true;
      setStoredPricingInvalid(false);
      setPricingRepairPending(true);
    }
    setPricingError('');
  };
  const editDeliveryNumber = (field, value) => updatePricing({ ...deliverySettings, [field]: value === '' ? '' : Number(value) });
  const editTier = (index, field, value) => updatePricing({ ...deliverySettings,
    distance_tiers: deliverySettings.distance_tiers.map((tier, i) => i === index ? { ...tier, [field]: value === '' ? '' : Number(value) } : tier),
  }, true);

  const [aiSettings, setAiSettings] = useState({
    enabled: true,
    model: 'gpt-5.2',
    auto_suggestions: true,
    response_tone: 'friendly'
  });
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    setRestaurantInfo(createRestaurantInfo());
    setBusinessHours(createBusinessHours());
  }, [selectedRestaurantId]);

  useEffect(() => {
    const current = { id: selectedRestaurantId, controller: new AbortController(), ready: false, busy: '', uncertain: false, invalidOrigin: false, invalidPricing: false, pricingRepairRequested: false, pricingRepairPending: false, pricingEdited: false, hadDistanceTiers: false, saves: new Set() };
    restaurantScope.current = current;
    setServiceCharge(null); setServiceError(''); setServiceMalformed(false); setServiceSaving(false);
    setDeliverySettings({ ...DEFAULT_DELIVERY_SETTINGS });
    setDeliveryCurrency(''); setPricingError('');
    setStoredPricingInvalid(false); setPricingRepairPending(false);
    setDetailsStatus('loading'); setLoadedRestaurantId(null); setSaveStatus(''); setOriginUncertain(false);
    setOriginInvalid(false); setOriginCooldownUntil(0);
    if (selectedRestaurantId) fetchRestaurantDetails(current);
    return () => { restaurantScope.current = null; current.controller.abort(); };
  }, [selectedRestaurantId, loadAttempt]);

  const fetchRestaurants = async () => {
    try {
     const { data } = await axios.get(
`${API}/restaurants`,
{ withCredentials: true }
);
      setRestaurants(data);
      if (data.length > 0) setSelectedRestaurantId(data[0].id);
    } catch (error) {
      toast.error('Unable to load restaurants. Please reload Settings.');
    }
  };

  const fetchRestaurantDetails = async (current) => {
    try {
      const { data } = await axios.get(`${API}/restaurants/${current.id}`, {
        withCredentials: true, signal: current.controller.signal,
      });
      if (restaurantScope.current !== current) return;
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error('Invalid restaurant details');
      }
      setRestaurantInfo({
        name: data.name || '',

        phone: data.phone || '',
        address: data.address || '',
        currency: data.currency || 'USD',
        tax_enabled: data.tax_enabled || false,
        tax_rate: data.tax_rate || 0,
        whatsapp_number: data.whatsapp_number || '',
        kitchen_whatsapp_number: data.kitchen_whatsapp_number || '',
        printer_enabled: data.printer_enabled || false,
        printer_type: data.printer_type || '',
        printer_connection: data.printer_connection || '',
        paper_size: data.paper_size || '80',
        printer_name: data.printer_name || '',
        auto_print: data.auto_print || false,
        copies: data.copies || 1,
        printer_roles: data.printer_roles || {
          ...DEFAULT_PRINTER_ROLES,
          kitchen: {
            ...DEFAULT_PRINTER_ROLES.kitchen,
            printer_name: data.printer_name || '',
            printer_type: data.printer_type || 'thermal',
            printer_connection: data.printer_connection || 'usb',
            paper_size: data.paper_size || '80',
          },
          cashier: {
            ...DEFAULT_PRINTER_ROLES.cashier,
            printer_name: data.printer_name || '',
            printer_type: data.printer_type || 'thermal',
            printer_connection: data.printer_connection || 'usb',
            paper_size: data.paper_size || '80',
          }
        },
        printer_auto_print: data.printer_auto_print || {
          manual_orders: Boolean(data.auto_print),
          whatsapp_orders: false,
          online_orders: false,
        },
        printer_copies_by_role: data.printer_copies_by_role || {
          kitchen: data.copies || 1,
          cashier: data.copies || 1,
        },
        printer_diagnostics: data.printer_diagnostics || {
          configuration_status: 'Not Configured',
          agent_status: 'Never Connected',
          hardware_status: 'Unknown',
          automatic_delivery_status: 'Inactive - Print Agent required',
          agent_last_seen_at: null,
        },
      });
      setServiceCharge(data.service_charge_settings ?? null);
      setServiceMalformed(Boolean(serviceErrorFor(data.service_charge_settings)));
      if (data.business_hours) setBusinessHours(data.business_hours);
      const { settings: normalized, invalidPricing } = normalizeDeliverySettings(data.delivery_settings);
      current.invalidPricing = invalidPricing;
      current.hadDistanceTiers = plainObject(data.delivery_settings) && Object.prototype.hasOwnProperty.call(data.delivery_settings, 'distance_tiers');
      current.pricingEdited = false;
      current.pricingRepairRequested = false;
      current.pricingRepairPending = false;
      setStoredPricingInvalid(invalidPricing);
      setPricingRepairPending(false);
      const invalidLocation = normalized.delivery_location != null && !validOrigin(normalized.delivery_location);
      if (invalidLocation) delete normalized.delivery_location;
      current.invalidOrigin = invalidLocation;
      setOriginInvalid(current.invalidOrigin);
      setDeliverySettings(normalized);
      try { setDeliveryCurrency(restaurantCurrencyLabel(data.currency)); }
      catch { setDeliveryCurrency('Currency unavailable'); }
      current.ready = true; setLoadedRestaurantId(current.id); setDetailsStatus('ready');
    } catch (error) {
      if (restaurantScope.current === current) setDetailsStatus('error');
    }
  };

  const beginOriginMutation = (reconcile = false) => {
    const current = restaurantScope.current;
    if (!canEditDelivery || !current?.ready || current.id !== selectedRestaurantId || current.busy || current.invalidPricing || current.pricingRepairPending || (current.uncertain && !reconcile)) return false;
    current.busy = 'origin'; setSaveStatus('origin'); return true;
  };
  const endOriginMutation = uncertain => {
    const current = restaurantScope.current;
    if (current?.id !== selectedRestaurantId) return;
    current.busy = ''; current.uncertain = uncertain;
    setSaveStatus(''); setOriginUncertain(uncertain);
  };
  const acceptOrigin = (location, id) => {
    if (restaurantScope.current?.id !== id || id !== selectedRestaurantId) return;
    restaurantScope.current.invalidOrigin = false; setOriginInvalid(false);
    setDeliverySettings(previous => ({ ...previous, delivery_location: location }));
  };
  const acceptOriginCooldown = (deadline, id) => {
    if (restaurantScope.current?.id !== id || id !== selectedRestaurantId) return;
    setOriginCooldownUntil(previous => Math.max(previous, deadline));
  };

  const handleSave = async (section) => {
    if (!selectedRestaurantId) {
      toast.error('Please create a restaurant first');
      return;
    }
    const current = restaurantScope.current;
    if (!current?.ready || current.id !== selectedRestaurantId) {
      toast.error('Wait for restaurant details to load before saving.'); return;
    }
    if (current.saves.has(section)) { toast.error('This section is already saving.'); return; }
    if (section === 'delivery') {
      const error = deliveryPricingError(deliverySettings);
      setPricingError(error || '');
      if (error) return;
      if (!canEditDelivery || current.busy || current.uncertain || (current.invalidOrigin && !current.pricingRepairPending) || current.invalidPricing) {
        toast.error('Confirm the delivery origin and finish any pending delivery save before saving delivery settings.'); return;
      }
      current.busy = section; setSaveStatus(section);
    }
    if (section === 'service_charge') {
      if (!canEditDelivery) return;
      const error = serviceMalformed ? 'Explicitly replace malformed settings before saving.' : serviceErrorFor(serviceCharge);
      setServiceError(error);
      if (error) return;
      setServiceSaving(true);
    }
    current.saves.add(section);
    try {
      let payload = {};
      if (section === 'restaurant') {
        const { printer_diagnostics, ...editableRestaurantInfo } = restaurantInfo;
        payload = editableRestaurantInfo;
      } else if (section === 'service_charge') {
        payload = { service_charge_settings: serviceCharge };
      } else if (section === 'hours') {
        payload = { business_hours: businessHours };
      } else if (section === 'delivery') {
        const { delivery_location, ...editableDeliverySettings } = deliverySettings;
        // UI defaults must not expand the exact legacy B5 write contract.
        if (!current.hadDistanceTiers && !current.pricingEdited) delete editableDeliverySettings.distance_tiers;
        payload = { delivery_settings: editableDeliverySettings };
      }

await axios.patch(
    `${API}/restaurants/${current.id}/settings`,
    payload,
    { withCredentials: true, signal: current.controller.signal }
);
      if (restaurantScope.current !== current) return;
      if (section === 'delivery' && current.pricingRepairPending) {
        current.pricingRepairPending = false;
        current.pricingRepairRequested = false;
        setPricingRepairPending(false);
        if (current.invalidOrigin) {
          current.invalidOrigin = false;
          setOriginInvalid(false);
        }
      }
      if (section === 'restaurant') {
        try { setDeliveryCurrency(restaurantCurrencyLabel(payload.currency)); }
        catch { setDeliveryCurrency('Currency unavailable'); }
      }
      toast.success(`${section} settings saved`);
    } catch (error) {
      if (restaurantScope.current !== current) return;
      toast.error(error.response?.status === 409
        ? 'Restaurant settings changed. Reload and review before saving again.' : 'Failed to save settings. Please try again.');
    } finally {
      current.saves.delete(section);
      if (restaurantScope.current === current && section === 'service_charge') setServiceSaving(false);
      if (restaurantScope.current === current && section === 'delivery') { current.busy = ''; setSaveStatus(''); }
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordError('');
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (passwordForm.next !== passwordForm.next.trim()) {
      setPasswordError('New password must not start or end with whitespace.');
      return;
    }
    if (Array.from(passwordForm.next).length < 12) {
      setPasswordError('New password must be at least 12 characters.');
      return;
    }
    setPasswordSaving(true);
    const result = await changePassword(passwordForm.current, passwordForm.next);
    setPasswordSaving(false);
    if (!result.success) {
      setPasswordError(result.error || 'Unable to change password.');
      return;
    }
    navigate('/login', { state: { message: result.message } });
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
        {t('settings')}
      </h1>

      <Card className="border-border/40">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Change Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="max-w-xl space-y-4" onSubmit={handleChangePassword} noValidate>
            {passwordError && <p className="text-sm text-destructive" role="alert">{passwordError}</p>}
            <div><Label htmlFor="current-password">Current password</Label><Input id="current-password" type="password" value={passwordForm.current} onChange={(event) => setPasswordForm({ ...passwordForm, current: event.target.value })} required data-testid="current-password" /></div>
            <div><Label htmlFor="new-password">New password</Label><Input id="new-password" type="password" value={passwordForm.next} onChange={(event) => setPasswordForm({ ...passwordForm, next: event.target.value })} required data-testid="new-password" /></div>
            <div><Label htmlFor="confirm-password">Confirm new password</Label><Input id="confirm-password" type="password" value={passwordForm.confirm} onChange={(event) => setPasswordForm({ ...passwordForm, confirm: event.target.value })} required data-testid="confirm-password" /></div>
            <p className="text-sm text-muted-foreground">Use at least 12 characters. You will need to sign in again after changing it.</p>
            <Button type="submit" disabled={passwordSaving} style={{ backgroundColor: '#E57200' }} data-testid="change-password-btn">{passwordSaving ? 'Saving...' : 'Change Password'}</Button>
          </form>
        </CardContent>
      </Card>

      {restaurants.length === 0 ? (
        <Card className="border-border/40">
          <CardContent className="py-12 text-center text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>
            No restaurants yet. Please create one from the Restaurants page first.
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="restaurant">
          <TabsList data-testid="settings-tabs" className="grid h-auto w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
            <TabsTrigger value="restaurant" data-testid="tab-restaurant" className="h-8 w-full px-2"><span className="flex items-center justify-center gap-2"><Store className="h-4 w-4 shrink-0" />Restaurant</span></TabsTrigger>
            <TabsTrigger value="hours" data-testid="tab-hours" className="h-8 w-full px-2"><span className="flex items-center justify-center gap-2"><Clock className="h-4 w-4 shrink-0" />Hours</span></TabsTrigger>
            <TabsTrigger value="delivery" data-testid="tab-delivery" className="h-8 w-full px-2"><span className="flex items-center justify-center gap-2"><Truck className="h-4 w-4 shrink-0" />Delivery</span></TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp" className="h-8 w-full px-2"><span className="flex items-center justify-center gap-2"><MessageSquare className="h-4 w-4 shrink-0" />WhatsApp</span></TabsTrigger>
            <TabsTrigger value="printer" className="h-8 w-full px-2"><span className="flex items-center justify-center gap-2"><Printer className="h-4 w-4 shrink-0" />Printer</span></TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai" className="h-8 w-full px-2"><span className="flex items-center justify-center gap-2"><Sparkles className="h-4 w-4 shrink-0" />AI</span></TabsTrigger>
          </TabsList>

          <TabsContent value="restaurant">
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Restaurant Information</CardTitle>
              </CardHeader>
              <div className="mb-4">
  <Label>Select Restaurant</Label>

  <Select
    value={selectedRestaurantId || ""}
    onValueChange={(value) => setSelectedRestaurantId(value)}
  >
    <SelectTrigger className="mt-2">
      <SelectValue placeholder="Choose Restaurant" />
    </SelectTrigger>

    <SelectContent>
      {restaurants.map((restaurant) => (
        <SelectItem
          key={restaurant.id}
          value={restaurant.id}
        >
          {restaurant.name}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
</div>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label>Restaurant Name</Label><Input value={restaurantInfo.name} onChange={(e) => setRestaurantInfo({ ...restaurantInfo, name: e.target.value })} data-testid="rest-name" /></div>
                  <RestaurantLogoUpload key={selectedRestaurantId} restaurantId={selectedRestaurantId} />
                  <div><Label>{t('phone')}</Label><Input value={restaurantInfo.phone} onChange={(e) => setRestaurantInfo({ ...restaurantInfo, phone: e.target.value })} data-testid="rest-phone" /></div>
                  <div><Label>Currency</Label>

<Select
  value={restaurantInfo.currency}
  onValueChange={(value) =>
    setRestaurantInfo({
      ...restaurantInfo,
      currency: value,
    })
  }
>
  <SelectTrigger data-testid="rest-currency">
    <SelectValue placeholder="Select Currency" />
  </SelectTrigger>

  <SelectContent>
    <SelectItem value="USD">🇺🇸 USD ($)</SelectItem>
    <SelectItem value="MYR">🇲🇾 MYR (RM)</SelectItem>
    <SelectItem value="SAR">🇸🇦 SAR (﷼)</SelectItem>
    <SelectItem value="AED">🇦🇪 AED (د.إ)</SelectItem>
    <SelectItem value="EUR">🇪🇺 EUR (€)</SelectItem>
    <SelectItem value="GBP">🇬🇧 GBP (£)</SelectItem>
    <SelectItem value="YER">🇾🇪 YER (﷼)</SelectItem>
    <SelectItem value="SGD">🇸🇬 SGD (S$)</SelectItem>
    <SelectItem value="CNY">🇨🇳 CNY (¥)</SelectItem>
    <SelectItem value="JPY">🇯🇵 JPY (¥)</SelectItem>
  </SelectContent>
</Select></div>
                </div>
                <div>
  <Label>SST Enabled</Label>
  <Switch
    checked={restaurantInfo.tax_enabled || false}
    onCheckedChange={(value) =>
      setRestaurantInfo({
        ...restaurantInfo,
        tax_enabled: value
      })
    }
  />
</div>

<div>
  <Label>SST Rate (%)</Label>
  <Input
    type="number"
    value={restaurantInfo.tax_rate || 0}
    onChange={(e) =>
      setRestaurantInfo({
        ...restaurantInfo,
        tax_rate: parseFloat(e.target.value) || 0
      })
    }
  />
</div>
                <div><Label>Address</Label><Textarea value={restaurantInfo.address} onChange={(e) => setRestaurantInfo({ ...restaurantInfo, address: e.target.value })} data-testid="rest-address" /></div>

                <Button onClick={() => handleSave('restaurant')} style={{ backgroundColor: '#E57200' }} data-testid="save-rest-btn">Save</Button>
              </CardContent>
            </Card>
            <Card className="border-border/40 mt-4">
              <CardHeader><CardTitle>Service Charge</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {detailsStatus === 'loading' && <p role="status">Loading service charge settings...</p>}
                {detailsStatus === 'error' && <p role="alert">Unable to load service charge settings.</p>}
                <Button type="button" variant="outline" disabled={serviceSaving} onClick={() => setLoadAttempt(value => value + 1)}>Reload service charge settings</Button>
                {serviceMalformed && <div role="alert">Stored service charge settings are malformed. Review an explicit replacement before saving.
                  {canEditDelivery && <Button type="button" onClick={() => { setServiceCharge({ enabled: false }); setServiceMalformed(false); setServiceError(''); }}>Replace malformed service charge settings</Button>}
                </div>}
                <fieldset data-testid="service-editor" disabled={!canEditDelivery || detailsStatus !== 'ready' || loadedRestaurantId !== selectedRestaurantId || serviceSaving || serviceMalformed} className="space-y-3">
                  <Label htmlFor="service-enabled">Service Charge Status</Label>
                  <select id="service-enabled" value={serviceCharge?.enabled === true ? 'enabled' : 'disabled'} onChange={e => editService({ enabled: e.target.value === 'enabled' })}>
                    <option value="disabled">Disabled</option><option value="enabled">Enabled</option>
                  </select>
                  <div><Label htmlFor="service-type">Charge Type</Label>
                    <select id="service-type" value={serviceCharge?.type ?? ''} onChange={e => editService({ type: e.target.value })}>
                      <option value="" disabled>Choose charge type</option><option value="percentage">Percentage</option><option value="fixed">Fixed</option>
                    </select>
                  </div>
                  <div><Label htmlFor="service-value">Charge Value ({serviceCharge?.type === 'percentage' ? '%' : deliveryCurrency})</Label>
                    <Input id="service-value" data-testid="service-value" type="number" min="0" max={serviceCharge?.type === 'percentage' ? 100 : undefined} step="any" value={serviceMalformed ? '' : serviceCharge?.value ?? ''} onChange={e => editService({ value: e.target.value === '' ? '' : Number(e.target.value) })} />
                  </div>
                  <div>Applies to {SERVICE_ORDERS.map(type => <label key={type} className="ml-3"><input type="checkbox" aria-label={`Service charge ${type}`} checked={Array.isArray(serviceCharge?.applies_to) && serviceCharge.applies_to.includes(type)} onChange={e => editService({ applies_to: e.target.checked ? [...(serviceCharge?.applies_to || []), type] : serviceCharge.applies_to.filter(item => item !== type) })} />{type.replace('_', ' ')}</label>)}</div>
                  {canEditDelivery && <Button type="button" data-testid="save-service-btn" onClick={() => handleSave('service_charge')} style={{ backgroundColor: '#E57200' }}>{serviceSaving ? 'Saving...' : 'Save Service Charge'}</Button>}
                </fieldset>
                {serviceError && <p role="alert">{serviceError}</p>}
                {serviceSaving && <p role="status">Saving service charge settings...</p>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hours">
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Business Hours</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {DAYS.map(day => (
                  <div key={day} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center pb-3 border-b border-border/40">
                    <div className="font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{day}</div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!businessHours[day]?.closed}
                        onCheckedChange={(c) => setBusinessHours({ ...businessHours, [day]: { ...businessHours[day], closed: !c } })}
                        data-testid={`hours-${day}-toggle`}
                      />
                      <Label className="text-sm">{businessHours[day]?.closed ? 'Closed' : 'Open'}</Label>
                    </div>
                    <Input type="time" disabled={businessHours[day]?.closed} value={businessHours[day]?.open || '09:00'} onChange={(e) => setBusinessHours({ ...businessHours, [day]: { ...businessHours[day], open: e.target.value } })} data-testid={`hours-${day}-open`} />
                    <Input type="time" disabled={businessHours[day]?.closed} value={businessHours[day]?.close || '22:00'} onChange={(e) => setBusinessHours({ ...businessHours, [day]: { ...businessHours[day], close: e.target.value } })} data-testid={`hours-${day}-close`} />
                  </div>
                ))}
                <Button onClick={() => handleSave('hours')} style={{ backgroundColor: '#E57200' }} data-testid="save-hours-btn">Save</Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="delivery">
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delivery Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">{restaurants.find(item => item.id === selectedRestaurantId)?.name}</p>
                {detailsStatus === 'loading' && <p role="status">Loading restaurant delivery settings...</p>}
                {detailsStatus === 'error' && <div role="alert">Unable to load restaurant delivery settings.
                  <Button type="button" variant="outline" onClick={() => setLoadAttempt(value => value + 1)}>Reload delivery settings</Button>
                </div>}
                {detailsStatus === 'ready' && loadedRestaurantId === selectedRestaurantId && <RestaurantDeliveryOrigin key={selectedRestaurantId}
                  restaurantId={selectedRestaurantId} location={deliverySettings.delivery_location}
                  canEdit={canEditDelivery} needsReconciliation={originUncertain} disabled={storedPricingInvalid || pricingRepairPending || Boolean(saveStatus && saveStatus !== 'origin')}
                  cooldownUntil={originCooldownUntil} onCooldown={acceptOriginCooldown}
                  beginMutation={beginOriginMutation} endMutation={endOriginMutation} onLocation={acceptOrigin} />}
                {originUncertain && <p role="alert">Delivery settings cannot be saved until the current origin is confirmed. Reload the origin above. Other settings can still be saved.</p>}
                {originInvalid && <p role="alert">{storedPricingInvalid
                  ? 'Stored delivery pricing and origin are malformed. Repair the delivery settings first; saving the validated repair will remove the invalid origin.'
                  : pricingRepairPending
                    ? 'Stored delivery origin is malformed. Save the repaired delivery settings to remove the invalid origin, then configure a verified origin if needed.'
                    : 'The stored delivery origin could not be verified. Select a verified origin before saving delivery settings. Other settings can still be saved.'}</p>}
                {storedPricingInvalid && <p role="alert">Stored delivery settings are malformed. Use the replacement action or pricing controls to create a valid replacement before saving delivery settings or changing the origin. Other settings can still be saved.</p>}
                <fieldset disabled={detailsStatus !== 'ready' || loadedRestaurantId !== selectedRestaurantId || !canEditDelivery || (saveStatus && saveStatus !== 'origin') || originUncertain}
                  className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch checked={deliverySettings.enabled === true} onCheckedChange={(c) => setDeliverySettings({ ...deliverySettings, enabled: c })} data-testid="delivery-enabled" />
                  <Label>Enable Delivery</Label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label htmlFor="delivery-pricing-mode">Pricing Method</Label>
                    <Select value={deliverySettings.pricing_mode || 'fixed'} onValueChange={value => updatePricing({ ...deliverySettings, pricing_mode: value }, true)}>
                      <SelectTrigger id="delivery-pricing-mode"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="fixed">Fixed Fee</SelectItem><SelectItem value="distance_tiers">Distance Based</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label htmlFor="delivery-min">Minimum Order ({deliveryCurrency})</Label><Input id="delivery-min" type="number" min="0" step="any" value={deliverySettings.min_order ?? ''} onChange={e => editDeliveryNumber('min_order', e.target.value)} data-testid="delivery-min" /></div>
                  {(deliverySettings.pricing_mode || 'fixed') === 'fixed' && <div><Label htmlFor="delivery-fee">Delivery Fee ({deliveryCurrency})</Label><Input id="delivery-fee" type="number" min="0" step="any" value={deliverySettings.delivery_fee ?? ''} onChange={e => editDeliveryNumber('delivery_fee', e.target.value)} data-testid="delivery-fee" /></div>}
                  <div><Label htmlFor="delivery-free">Free Delivery Above ({deliveryCurrency})</Label><Input id="delivery-free" type="number" min="0" step="any" value={deliverySettings.free_delivery_threshold ?? ''} onChange={e => editDeliveryNumber('free_delivery_threshold', e.target.value)} data-testid="delivery-free" /></div>
                  <div><Label htmlFor="delivery-radius">Radius (km)</Label><Input id="delivery-radius" type="number" min="0" step="any" value={deliverySettings.delivery_radius_km ?? ''} onChange={e => editDeliveryNumber('delivery_radius_km', e.target.value)} data-testid="delivery-radius" /></div>
                  <div><Label htmlFor="delivery-time">Estimated Time (minutes)</Label><Input id="delivery-time" type="number" min="1" step="1" value={deliverySettings.estimated_time_minutes ?? ''} onChange={e => editDeliveryNumber('estimated_time_minutes', e.target.value)} data-testid="delivery-time" /></div>
                </div>
                {storedPricingInvalid && <Button type="button" variant="outline" data-testid="replace-invalid-pricing" onClick={() => updatePricing({ ...deliverySettings, pricing_mode: 'fixed', distance_tiers: [] }, true)}>Replace invalid delivery settings with Fixed Fee</Button>}
                {deliverySettings.pricing_mode === 'distance_tiers' && <section aria-label="Distance Pricing Tiers" className="space-y-3">
                  <h3 className="font-semibold">Distance Pricing Tiers</h3>
                  <p className="text-sm text-muted-foreground">Add up to 10 tiers in increasing distance order. The final tier must cover the delivery radius.</p>
                  {deliverySettings.distance_tiers.map((tier, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div><Label htmlFor={`tier-distance-${index}`}>Up to (km) — tier {index + 1}</Label><Input id={`tier-distance-${index}`} data-testid={`tier-distance-${index}`} type="number" min="0" step="any" value={tier.up_to_km ?? ''} onChange={e => editTier(index, 'up_to_km', e.target.value)} /></div>
                    <div><Label htmlFor={`tier-fee-${index}`}>Delivery Fee ({deliveryCurrency}) — tier {index + 1}</Label><Input id={`tier-fee-${index}`} data-testid={`tier-fee-${index}`} type="number" min="0" step="any" value={tier.fee ?? ''} onChange={e => editTier(index, 'fee', e.target.value)} /></div>
                    <Button type="button" variant="outline" aria-label={`Remove tier ${index + 1}`} onClick={() => updatePricing({ ...deliverySettings, distance_tiers: deliverySettings.distance_tiers.filter((_, i) => i !== index) }, true)}>Remove</Button>
                  </div>)}
                  <Button type="button" variant="outline" data-testid="add-delivery-tier" disabled={deliverySettings.distance_tiers.length >= 10} onClick={() => { if (deliverySettings.distance_tiers.length < 10) updatePricing({ ...deliverySettings, distance_tiers: [...deliverySettings.distance_tiers, { up_to_km: '', fee: '' }] }, true); }}>Add Tier</Button>
                </section>}
                {pricingError && <p role="alert">{pricingError}</p>}
                {canEditDelivery && <Button onClick={() => handleSave('delivery')} disabled={Boolean(saveStatus) || originUncertain || (originInvalid && !pricingRepairPending) || storedPricingInvalid}
                  style={{ backgroundColor: '#E57200' }} data-testid="save-delivery-btn">{saveStatus === 'delivery' ? 'Saving...' : 'Save'}</Button>}
                </fieldset>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="whatsapp">
            <SettingsWhatsAppTab
              restaurantInfo={restaurantInfo}
              setRestaurantInfo={setRestaurantInfo}
              handleSave={handleSave}
            />
          </TabsContent>

         <TabsContent value="printer">
          <PrinterSettings
    restaurantInfo={restaurantInfo}
    setRestaurantInfo={setRestaurantInfo}
    handleSave={handleSave}
    selectedRestaurantId={selectedRestaurantId}
            canAdministerPrinters={["super_admin", "company_admin", "restaurant_manager"].includes(user?.role)}
            canRetryPrintJobs={["super_admin", "company_admin", "restaurant_manager", "supervisor"].includes(user?.role)}
  />
</TabsContent>

          <TabsContent value="ai">
            <Card className="border-border/40">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>AI Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch checked={aiSettings.enabled} onCheckedChange={(c) => setAiSettings({ ...aiSettings, enabled: c })} data-testid="ai-enabled" />
                  <Label>Enable AI Assistant (GPT-5.2)</Label>
                </div>
                <div><Label>Model</Label><Input value={aiSettings.model} onChange={(e) => setAiSettings({ ...aiSettings, model: e.target.value })} data-testid="ai-model" /></div>
                <div><Label>Response Tone</Label><Input value={aiSettings.response_tone} onChange={(e) => setAiSettings({ ...aiSettings, response_tone: e.target.value })} data-testid="ai-tone" /></div>
                <div className="flex items-center gap-2">
                  <Switch checked={aiSettings.auto_suggestions} onCheckedChange={(c) => setAiSettings({ ...aiSettings, auto_suggestions: c })} data-testid="ai-suggestions" />
                  <Label>Auto Suggestions</Label>
                </div>
                <Button onClick={() => toast.success('AI settings saved')} style={{ backgroundColor: '#E57200' }} data-testid="save-ai-btn">Save</Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Settings;
